/**
 * Archive Manager - Advanced functionality for locked content
 * Handles email verification, code generation, and content unlocking
 */

class AdvancedArchiveManager {
  constructor(sectionId, options = {}) {
    this.sectionId = sectionId;
    this.options = {
      codeLength: 6,
      codeExpiry: 15 * 60 * 1000, // 15 minutes
      maxAttempts: 3,
      ...options
    };
    
    this.attempts = 0;
    this.generatedCodes = new Map();
    this.init();
  }

  init() {
    this.gallery = document.getElementById(`archive-gallery-${this.sectionId}`);
    this.modal = document.getElementById(`unlock-modal-${this.sectionId}`);
    this.unlockForm = document.getElementById(`unlock-form-${this.sectionId}`);
    this.codeForm = document.getElementById(`code-form-${this.sectionId}`);
    this.codeSubmitForm = document.getElementById(`code-submit-form-${this.sectionId}`);
    
    // Check if archive is already unlocked
    if (this.isArchiveUnlocked()) {
      this.unlockArchive();
    }
    
    // Add event listeners
    this.addEventListeners();
  }

  addEventListeners() {
    // Close modal on outside click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.style.display === 'flex') {
        this.closeModal();
      }
    });

    // Auto-format code input
    const codeInput = document.getElementById(`unlock-code-${this.sectionId}`);
    if (codeInput) {
      codeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/\D/g, '').slice(0, this.options.codeLength);
      });
    }
  }

  showUnlockForm() {
    this.modal.style.display = 'flex';
    this.unlockForm.style.display = 'block';
    this.codeForm.style.display = 'none';
    this.clearMessages();
    
    // Focus on email input
    const emailInput = document.getElementById(`email-${this.sectionId}`);
    if (emailInput) {
      emailInput.focus();
    }
  }

  closeModal() {
    this.modal.style.display = 'none';
    this.resetForm();
  }

  resetForm() {
    if (this.unlockForm) {
      this.unlockForm.reset();
    }
    if (this.codeSubmitForm) {
      this.codeSubmitForm.reset();
    }
    this.clearMessages();
  }

  async submitEmail(event) {
    event.preventDefault();
    
    const email = event.target.email.value.trim();
    const submitBtn = event.target.querySelector('.submit-btn');
    
    if (!this.validateEmail(email)) {
      this.showMessage('Please enter a valid email address.', 'error');
      return;
    }
    
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
    
    try {
      const result = await this.sendEmailToServer(email);
      
      if (result.success) {
        // Generate and store code
        const code = this.generateCode();
        this.storeCode(email, code);
        
        // Send code to user (in real implementation, this would be via email service)
        this.sendCodeToUser(email, code);
        
        this.showMessage('Email sent successfully! Check your inbox for the unlock code.', 'success');
        
        // Show code form
        this.unlockForm.style.display = 'none';
        this.codeForm.style.display = 'block';
        
        // Focus on code input
        const codeInput = document.getElementById(`unlock-code-${this.sectionId}`);
        if (codeInput) {
          codeInput.focus();
        }
      } else {
        this.showMessage(result.message || 'Failed to send email. Please try again.', 'error');
      }
      
    } catch (error) {
      console.error('Email submission error:', error);
      this.showMessage('Failed to send email. Please try again.', 'error');
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Get Unlock Code';
    }
  }

  async submitCode(event) {
    event.preventDefault();
    
    const code = event.target.code.value.trim();
    const submitBtn = event.target.querySelector('.submit-btn');
    
    if (!code || code.length !== this.options.codeLength) {
      this.showMessage(`Please enter a ${this.options.codeLength}-digit code.`, 'error');
      return;
    }
    
    submitBtn.disabled = true;
    submitBtn.textContent = 'Verifying...';
    
    try {
      const isValid = await this.verifyCode(code);
      
      if (isValid) {
        this.showMessage('Code verified successfully! Archive unlocked.', 'success');
        this.unlockArchive();
        
        // Auto-close modal after success
        setTimeout(() => this.closeModal(), 2000);
      } else {
        this.attempts++;
        const remainingAttempts = this.options.maxAttempts - this.attempts;
        
        if (remainingAttempts > 0) {
          this.showMessage(`Invalid code. ${remainingAttempts} attempts remaining.`, 'error');
        } else {
          this.showMessage('Too many failed attempts. Please request a new code.', 'error');
          this.resetToEmailForm();
        }
      }
      
    } catch (error) {
      console.error('Code verification error:', error);
      this.showMessage('Failed to verify code. Please try again.', 'error');
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Unlock Archive';
    }
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  generateCode() {
    return Math.random().toString().substr(2, this.options.codeLength);
  }

  storeCode(email, code) {
    const expiry = Date.now() + this.options.codeExpiry;
    this.generatedCodes.set(email, { code, expiry });
    
    // Clean up expired codes
    this.cleanupExpiredCodes();
  }

  cleanupExpiredCodes() {
    const now = Date.now();
    for (const [email, data] of this.generatedCodes.entries()) {
      if (data.expiry < now) {
        this.generatedCodes.delete(email);
      }
    }
  }

  async sendCodeToUser(email, code) {
    // In a real implementation, this would send an email via your email service
    // For demo purposes, we'll just log it
    console.log(`Unlock code for ${email}: ${code}`);
    
    // You could integrate with services like:
    // - Shopify's email functionality
    // - Third-party email services (SendGrid, Mailgun, etc.)
    // - Custom email server
  }

  async sendEmailToServer(email) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Here you would make a POST request to your server
    // Example implementation:
    /*
    try {
      const response = await fetch('/api/archive/request-code', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ 
          email, 
          sectionId: this.sectionId,
          timestamp: Date.now()
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
    */
    
    // Mock response for demo
    return { success: true, message: 'Code sent successfully' };
  }

  async verifyCode(code) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Check if code exists and is valid
    for (const [email, data] of this.generatedCodes.entries()) {
      if (data.code === code && data.expiry > Date.now()) {
        // Code is valid, remove it from storage
        this.generatedCodes.delete(email);
        return true;
      }
    }
    
    return false;
  }

  unlockArchive() {
    // Store unlock status
    localStorage.setItem(`archive-${this.sectionId}-unlocked`, 'true');
    localStorage.setItem(`archive-${this.sectionId}-unlocked-at`, Date.now().toString());
    
    // Remove blur from images
    const images = this.gallery.querySelectorAll('.archive-image');
    images.forEach(img => {
      img.classList.add('unlocked');
      // Load high-resolution image
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
      }
    });
    
    // Hide overlays with animation
    const overlays = this.gallery.querySelectorAll('.archive-overlay');
    overlays.forEach(overlay => {
      overlay.style.transition = 'opacity 0.5s ease';
      overlay.classList.add('hidden');
    });
    
    // Add unlocked class to gallery
    this.gallery.classList.add('unlocked');
    
    // Trigger custom event
    this.gallery.dispatchEvent(new CustomEvent('archiveUnlocked', {
      detail: { sectionId: this.sectionId }
    }));
  }

  isArchiveUnlocked() {
    const unlocked = localStorage.getItem(`archive-${this.sectionId}-unlocked`);
    if (!unlocked) return false;
    
    // Check if unlock is still valid (optional: add expiry)
    const unlockedAt = localStorage.getItem(`archive-${this.sectionId}-unlocked-at`);
    if (unlockedAt) {
      const unlockTime = parseInt(unlockedAt);
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      if (now - unlockTime > maxAge) {
        // Unlock expired, remove from storage
        localStorage.removeItem(`archive-${this.sectionId}-unlocked`);
        localStorage.removeItem(`archive-${this.sectionId}-unlocked-at`);
        return false;
      }
    }
    
    return true;
  }

  resetToEmailForm() {
    this.unlockForm.style.display = 'block';
    this.codeForm.style.display = 'none';
    this.attempts = 0;
    this.clearMessages();
    
    // Focus on email input
    const emailInput = document.getElementById(`email-${this.sectionId}`);
    if (emailInput) {
      emailInput.focus();
    }
  }

  showMessage(message, type) {
    this.clearMessages();
    
    const messageDiv = document.createElement('div');
    messageDiv.className = `${type}-message`;
    messageDiv.textContent = message;
    
    this.modal.querySelector('.unlock-modal-content').appendChild(messageDiv);
    
    // Auto-hide error messages after 5 seconds
    if (type === 'error') {
      setTimeout(() => {
        if (messageDiv.parentNode) {
          messageDiv.remove();
        }
      }, 5000);
    }
  }

  clearMessages() {
    const messages = this.modal.querySelectorAll('.success-message, .error-message');
    messages.forEach(msg => msg.remove());
  }

  // Public method to manually unlock archive (for admin purposes)
  forceUnlock() {
    this.unlockArchive();
  }

  // Public method to manually lock archive
  forceLock() {
    localStorage.removeItem(`archive-${this.sectionId}-unlocked`);
    localStorage.removeItem(`archive-${this.sectionId}-unlocked-at`);
    
    const images = this.gallery.querySelectorAll('.archive-image');
    images.forEach(img => img.classList.remove('unlocked'));
    
    const overlays = this.gallery.querySelectorAll('.archive-overlay');
    overlays.forEach(overlay => overlay.classList.remove('hidden'));
    
    this.gallery.classList.remove('unlocked');
  }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AdvancedArchiveManager;
} else {
  window.AdvancedArchiveManager = AdvancedArchiveManager;
}